import { getJson, pathSegment, postJsonWithoutToken, queryString } from "./client";

/**
 * The catalogue: the one store of postings and the boards they come
 * from, the same for everyone and holding nothing of a person's. Read
 * only. The shapes are `libs/crates/api/src/catalogue_api`.
 */
export const CATALOGUE_BASE = "/v1/catalogue";

export type Source =
    | "GREENHOUSE"
    | "LEVER"
    | "ASHBY"
    | "WORKABLE"
    | "SMARTRECRUITERS"
    | "RIPPLING"
    | "RECRUITEE"
    | "BAMBOOHR"
    | "BREEZY"
    | "WORKDAY";
export type WorkSetting = "REMOTE" | "HYBRID" | "ON_SITE";
export type Employment = "FULL_TIME" | "PART_TIME" | "CONTRACT" | "INTERNSHIP" | "TEMPORARY";
export type PayInterval = "YEAR" | "MONTH" | "WEEK" | "DAY" | "HOUR";

export interface StatedPay {
    currency: string;
    min: number | null;
    max: number | null;
    /** What the board said. Pay floors go by this alone. */
    interval: PayInterval | null;
    /** The period to tell a person, as the catalogue reads the figures. */
    period?: PayInterval | null;
    text?: string | null;
}

export interface PostingKeyword {
    term: string;
    required: boolean;
    spellings?: string[];
    kind?: string | null;
    quote?: string | null;
}

export interface PostingSummary {
    posting_id: string;
    board_id: string;
    source: Source;
    board: string;
    external_id: string;
    company: string;
    /** The company's logo, a 256x256 PNG. Absent when none was found. */
    logo_url?: string;
    title: string;
    url: string;
    apply_url: string | null;
    location: string | null;
    locations: string[];
    country: string | null;
    regions: string[];
    cities: string[];
    work_setting: WorkSetting | null;
    department: string | null;
    team: string | null;
    employment: Employment | null;
    employment_label: string | null;
    pay: StatedPay | null;
    posted_at: string | null;
    read: string;
    version: number;
    first_seen_at: string;
    last_seen_at: string;
    closed_at: string | null;
    updated_at: string;
}

/** One posting, whole, open or closed: `catalogue_api::responses::Posting`. */
export interface Posting extends PostingSummary {
    description: string;
    keywords: PostingKeyword[];
    /** The model's reading of the text. Absent until the posting has been read. */
    reading: PostingReading | null;
}

export interface CatalogueStatus {
    as_of: string;
    boards: { active: number; paused: number; removed: number; dead: number };
    failing: number;
    last_checked_at: string | null;
    next_check_at: string | null;
    checked_last_hour: number;
    checked_last_day: number;
    open_postings: number;
    found_last_day: number;
    closed_last_day: number;
}

export interface PostingEvent {
    event_id: string;
    posting_id: string;
    /** Its place in the posting's record, from one. */
    seq: number;
    /** `DISCOVERED`, `CHANGED`, `CLOSED` or `REOPENED`. */
    kind: string;
    /** The posting as it read, for a discovery and a change. */
    payload: unknown;
    occurred_at: string;
}

export interface PostingEventList {
    data: PostingEvent[];
}

export function getCatalogueStatus(): Promise<CatalogueStatus> {
    return getJson(`${CATALOGUE_BASE}/status`);
}

/** One posting, open or closed, with its text. */
export async function getPosting(postingId: string): Promise<Posting> {
    return getJson(`${CATALOGUE_BASE}/postings/${pathSegment(postingId)}`);
}

/** Everything that happened to one posting, in order: found, changed, closed, listed again. */
export async function getPostingEvents(postingId: string): Promise<PostingEventList> {
    return getJson(`${CATALOGUE_BASE}/postings/${pathSegment(postingId)}/events`);
}

/** How recently a posting went up: `24h`, `3d` or `7d`. `catalogue_api::requests::PostedWithin`. */
export type PostedWithin = "24h" | "3d" | "7d";

/** How senior the job is, as the model read the posting. `catalogue_api::reading::Seniority`. */
export type Seniority =
    "INTERNSHIP" | "ENTRY" | "MID" | "SENIOR" | "LEAD" | "MANAGER" | "DIRECTOR" | "EXECUTIVE";

export const SENIORITIES: readonly Seniority[] = [
    "INTERNSHIP",
    "ENTRY",
    "MID",
    "SENIOR",
    "LEAD",
    "MANAGER",
    "DIRECTOR",
    "EXECUTIVE",
];

/** The kind of job, from a closed list of twenty five and `OTHER`. `catalogue_api::reading::JobFamily`. */
export type JobFamily =
    | "SOFTWARE_ENGINEERING"
    | "DATA_AND_ANALYTICS"
    | "IT_AND_INFRASTRUCTURE"
    | "SECURITY"
    | "PRODUCT_MANAGEMENT"
    | "DESIGN"
    | "PROJECT_AND_PROGRAM_MANAGEMENT"
    | "SALES"
    | "MARKETING"
    | "CUSTOMER_SUPPORT_AND_SUCCESS"
    | "FINANCE_AND_ACCOUNTING"
    | "PEOPLE_AND_RECRUITING"
    | "LEGAL_AND_COMPLIANCE"
    | "OPERATIONS_AND_ADMINISTRATION"
    | "SUPPLY_CHAIN_AND_LOGISTICS"
    | "MANUFACTURING_AND_PRODUCTION"
    | "SKILLED_TRADES"
    | "ENGINEERING"
    | "SCIENCE_AND_RESEARCH"
    | "NURSING"
    | "HEALTHCARE"
    | "EDUCATION_AND_TRAINING"
    | "HOSPITALITY_AND_FOOD_SERVICE"
    | "RETAIL"
    | "MEDIA_AND_COMMUNICATIONS"
    | "OTHER";

/** Every family, `OTHER` last, as the catalogue lists them. */
export const JOB_FAMILIES: readonly JobFamily[] = [
    "SOFTWARE_ENGINEERING",
    "DATA_AND_ANALYTICS",
    "IT_AND_INFRASTRUCTURE",
    "SECURITY",
    "PRODUCT_MANAGEMENT",
    "DESIGN",
    "PROJECT_AND_PROGRAM_MANAGEMENT",
    "SALES",
    "MARKETING",
    "CUSTOMER_SUPPORT_AND_SUCCESS",
    "FINANCE_AND_ACCOUNTING",
    "PEOPLE_AND_RECRUITING",
    "LEGAL_AND_COMPLIANCE",
    "OPERATIONS_AND_ADMINISTRATION",
    "SUPPLY_CHAIN_AND_LOGISTICS",
    "MANUFACTURING_AND_PRODUCTION",
    "SKILLED_TRADES",
    "ENGINEERING",
    "SCIENCE_AND_RESEARCH",
    "NURSING",
    "HEALTHCARE",
    "EDUCATION_AND_TRAINING",
    "HOSPITALITY_AND_FOOD_SERVICE",
    "RETAIL",
    "MEDIA_AND_COMMUNICATIONS",
    "OTHER",
];

/** The model's reading of a posting's text. `catalogue_api::reading::PostingReading`. */
export interface PostingReading {
    family: JobFamily;
    seniority?: Seniority | null;
    role: string;
    years_asked?: number | null;
    work_setting?: WorkSetting | null;
    employment?: Employment | null;
    countries?: string[];
    pay?: StatedPay | null;
}

/** A search's order. Best fit is the resume service's, not the catalogue's. */
export type SearchSort = "NEARBY" | "RELEVANCE" | "NEWEST" | "POSTED" | "PAY";

/**
 * A search of the open postings, `catalogue_api::requests::SearchPostingsQuery`.
 * Every field is optional; one left out narrows nothing. With `unstated`
 * the filters read as an envoy reads them: a posting is never ruled out
 * by what its board did not say.
 */
export interface SearchPostingsQuery {
    q?: string;
    title?: readonly string[];
    skip?: readonly string[];
    mention?: readonly string[];
    source?: Source;
    board?: string;
    /** Every board of this company with `source`, by the part of a slug before its first dot. */
    company?: string;
    employment?: readonly Employment[];
    work_setting?: readonly WorkSetting[];
    country?: readonly string[];
    region?: readonly string[];
    city?: readonly string[];
    /**
     * Where the visitor is, as the edge says (`US`, `WA`). With no country,
     * region or city, the catalogue opens the search on the narrowest of
     * them with enough open postings, as `nearby` reads places/here.
     */
    here_country?: string;
    here_region?: string;
    seniority?: readonly Seniority[];
    family?: readonly JobFamily[];
    language?: readonly string[];
    unstated?: boolean;
    /** Keep remote postings whatever region or city is asked, still held to the country. */
    remote_country_wide?: boolean;
    location?: string;
    pay_currency?: string;
    pay_at_least?: number;
    pay_at_most?: number;
    pay_unstated?: boolean;
    posted_within?: PostedWithin;
    /**
     * Only postings worked on site within this many miles of where the edge
     * says the person is (1 to 250), with remote work they can take kept.
     * Refused with a 400 when the edge does not say where they are and no
     * point is named.
     */
    within_miles?: number;
    /** With `near_longitude`, where `within_miles` is measured from, in place of the edge's reading. */
    near_latitude?: number;
    near_longitude?: number;
    /** The country remote work is held to under `within_miles`, in place of the edge's reading. */
    near_country?: string;
    sort?: SearchSort;
    next_cursor?: string;
    limit?: number;
}

/** A posting found by a search, whole, with its text, keywords and reading. */
export interface SearchPosting extends PostingSummary {
    description: string;
    keywords: PostingKeyword[];
    reading: PostingReading | null;
    /**
     * Of the places it names, the one nearest the person, spelled as `cities`
     * spells one. Only in a search that knows where they are.
     */
    nearest_place?: string | null;
    /** Where the cities it names are, one point for each the catalogue could place. Absent when none. */
    points?: GeoPoint[];
}

export interface SearchResults {
    data: SearchPosting[];
    next_cursor: string | null;
}

export interface WorkSettingCounts {
    remote: number;
    hybrid: number;
    on_site: number;
    unstated: number;
}

export interface BoardCount {
    board_id: string;
    source: Source;
    board: string;
    company: string;
    postings: number;
}

/**
 * How many a search finds, and how they divide. Each division leaves its
 * own filter out: `work_settings` counts without `work_setting`, and
 * `pay_unstated` without `pay_at_least`.
 */
export interface SearchCounts {
    total: number;
    work_settings: WorkSettingCounts;
    pay_unstated: number;
    boards: BoardCount[];
    boards_total: number;
}

export type PlaceLevel = "COUNTRY" | "REGION" | "CITY";

/** A point on the map, in degrees. `common::geo::GeoPoint`. */
export interface GeoPoint {
    latitude: number;
    longitude: number;
}

export interface Place {
    level: PlaceLevel;
    /** What a filter takes: an ISO code, or a city as the postings spell it. */
    value: string;
    name: string;
    country: string | null;
    region: string | null;
    postings: number;
    /** For a city the catalogue can place on the map, where it is. Absent otherwise. */
    point?: GeoPoint | null;
}

export interface PlaceList {
    data: Place[];
    counted_at: string | null;
}

/** Where the caller is, as the edge in front of the API says, with the open postings at each. */
export interface Here {
    country: Place | null;
    region: Place | null;
    city: Place | null;
    counted_at: string | null;
}

export interface Title {
    title: string;
    postings: number;
}

export interface TitleList {
    data: Title[];
    counted_at: string | null;
}

/** A search as the query string the catalogue reads, a pair a value. */
export function searchQueryString(query: SearchPostingsQuery): string {
    return queryString({
        q: query.q,
        title: query.title,
        skip: query.skip,
        mention: query.mention,
        source: query.source,
        board: query.board,
        company: query.company,
        employment: query.employment,
        work_setting: query.work_setting,
        country: query.country,
        region: query.region,
        city: query.city,
        here_country: query.here_country,
        here_region: query.here_region,
        seniority: query.seniority,
        family: query.family,
        language: query.language,
        unstated: query.unstated,
        remote_country_wide: query.remote_country_wide,
        location: query.location,
        pay_currency: query.pay_currency,
        pay_at_least: query.pay_at_least,
        pay_at_most: query.pay_at_most,
        pay_unstated: query.pay_unstated,
        posted_within: query.posted_within,
        within_miles: query.within_miles,
        near_latitude: query.near_latitude,
        near_longitude: query.near_longitude,
        near_country: query.near_country,
        sort: query.sort,
        next_cursor: query.next_cursor,
        limit: query.limit,
    });
}

export function searchPostings(query: SearchPostingsQuery): Promise<SearchResults> {
    return getJson(`${CATALOGUE_BASE}/search${searchQueryString(query)}`);
}

export function searchCounts(query: SearchPostingsQuery): Promise<SearchCounts> {
    return getJson(`${CATALOGUE_BASE}/search/counts${searchQueryString(query)}`);
}

/** Places to pick from: with nothing typed, the ones with the most open postings first. */
export function getPlaces(params: {
    level: PlaceLevel;
    country?: readonly string[];
    region?: readonly string[];
    q?: string;
    limit?: number;
}): Promise<PlaceList> {
    return getJson(
        `${CATALOGUE_BASE}/places${queryString({
            level: params.level,
            country: params.country,
            region: params.region,
            q: params.q,
            limit: params.limit,
        })}`,
    );
}

/** Where the caller is, by their address. Never cached for anyone else. */
export function getHere(): Promise<Here> {
    return getJson(`${CATALOGUE_BASE}/places/here`);
}

/** Short job titles to pick from: with nothing typed, the most posted first. */
export function getTitles(q?: string, limit?: number): Promise<TitleList> {
    return getJson(`${CATALOGUE_BASE}/titles${queryString({ q, limit })}`);
}

/** The most of a pasted job's text the client sends, so the body stays under the route's 64 KB. */
// At 3 bytes a character this keeps the body under the route's 64 KB.
const JOB_TEXT_LIMIT = 20_000;
const JOB_TITLE_LIMIT = 300;

/** The first `limit` characters of `text`, never ending inside a surrogate pair. */
function cut(text: string, limit: number): string {
    if (text.length <= limit) return text;
    const last = text.charCodeAt(limit - 1);
    const splitsPair = last >= 0xd800 && last <= 0xdbff;
    return text.slice(0, splitsPair ? limit - 1 : limit);
}

/**
 * The skills a pasted job names, as the catalogue finds a posting's own.
 * Open to visitors, so no token goes with it. Nothing is stored. The text
 * is cut to what the route reads, so a long paste is not refused whole.
 */
export function jobKeywords(
    title: string,
    description: string,
): Promise<{ keywords: PostingKeyword[] }> {
    return postJsonWithoutToken(`${CATALOGUE_BASE}/job-keywords`, {
        title: cut(title, JOB_TITLE_LIMIT),
        description: cut(description, JOB_TEXT_LIMIT),
    });
}
